import { type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedArticles, RelatedServices } from "@/components/RelatedLinks";
import { SITE } from "@/lib/seo";
import jsonLd from "@/data/ico-met-police-third-party-suppliers-jsonld.json";

/**
 * Dedicated article route modelled on the branded uniforms news route. Copy is the
 * approved article verbatim; JSON-LD is emitted exactly as supplied in the data file.
 */

const SLUG = "ico-met-police-third-party-suppliers-disposal";
const URL = `${SITE}/blog/${SLUG}`;
const TITLE_TAG = "ICO and the Met Police: Choosing Suppliers | Preserve Earth";
const META_DESCRIPTION =
  "The ICO's action against the Met Police is about unapproved third-party tools. Why the same checks apply to whoever destroys your confidential waste.";
const H1 = "What does the ICO's action against the Met Police mean for choosing your suppliers?";
const HERO_SRC = "/images/blog/ico-met-police-third-party-suppliers-hero.webp";
const OG_IMAGE = `${SITE}/images/blog/ico-met-police-third-party-suppliers-og.jpg`;
const HERO_ALT =
  "Two colleagues reviewing a printed supplier contract and checklist at an office table, with a grey lockable confidential waste console behind them";
const DATE = "2026-10-26";

const crumbs = [
  { name: "Blog", path: "/blog" },
  { name: H1, path: `/blog/${SLUG}` },
];

export const Route = createFileRoute("/blog/ico-met-police-third-party-suppliers-disposal")({
  head: () => ({
    meta: [
      { title: TITLE_TAG },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: TITLE_TAG },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: HERO_ALT },
      { property: "article:published_time", content: DATE },
      { property: "article:modified_time", content: DATE },
      { property: "article:section", content: "Data Protection" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE_TAG },
      { name: "twitter:description", content: META_DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: HERO_ALT },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: (jsonLd as object[]).map((node) => ({
      type: "application/ld+json",
      children: JSON.stringify(node),
    })),
  }),
  component: IcoMetPoliceSuppliersPage,
});

const linkClass =
  "underline decoration-moss/40 underline-offset-4 transition-colors hover:text-moss";
const h2Class = "mt-14 font-serif text-3xl leading-tight text-charcoal lg:text-4xl";
const bodyClass = "mt-5 text-pretty text-base leading-[1.8] text-charcoal/80";
const listClass =
  "mt-6 list-disc space-y-3 pl-6 text-base leading-[1.7] text-charcoal/80 marker:text-moss";

function A({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} className={linkClass}>
      {children}
    </a>
  );
}

function IcoMetPoliceSuppliersPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader
        links={[
          { label: "Home", href: "/" },
          { label: "About", href: "/about" },
          { label: "Services", href: "/services" },
          { label: "Blog", href: "/blog" },
        ]}
        enquireHref="/#enquire"
      />

      <main>
        <article>
          <header className="container-page pt-16 pb-10">
            <Breadcrumbs crumbs={crumbs} />
            <p className="mb-6 text-xs font-medium uppercase tracking-[0.25em] text-moss">
              Data Protection · 6 min read
            </p>
            <h1 className="max-w-[28ch] text-balance font-serif text-4xl leading-[1.05] text-charcoal md:text-5xl lg:text-6xl">
              {H1}
            </h1>
            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-charcoal/70">
              By Preserve Earth team, Last updated <time dateTime={DATE}>26 October 2026</time>
            </p>
          </header>

          <div className="container-page">
            <figure className="overflow-hidden rounded-md outline outline-1 -outline-offset-1 outline-charcoal/10">
              <img
                src={HERO_SRC}
                alt={HERO_ALT}
                width={1280}
                height={720}
                loading="eager"
                fetchPriority="high"
                className="aspect-[16/9] w-full object-cover"
              />
            </figure>
          </div>

          <div className="container-page py-20">
            <div className="grid gap-16 lg:grid-cols-12">
              <aside className="lg:col-span-3">
                <div className="sticky top-24 space-y-6 border-l border-moss/30 pl-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    The Ledger
                  </p>
                  <p className="text-sm leading-relaxed text-charcoal/70">
                    Field notes on compliant, documented disposal for UK businesses.
                  </p>
                  <Link
                    to="/blog"
                    className="block text-xs font-medium uppercase tracking-[0.2em] text-moss underline decoration-moss/30 underline-offset-4"
                  >
                    All articles
                  </Link>
                </div>
              </aside>

              <div className="lg:col-span-9 lg:max-w-[68ch]">
                <p className="text-pretty text-lg leading-[1.75] text-charcoal">
                  <strong>Short answer:</strong> On 14 October 2026 the ICO announced a reprimand
                  and enforcement notice against the Metropolitan Police Service after officers and
                  staff shared personal information on a third-party platform the force had never
                  formally approved. The case has nothing to do with waste, but the lesson carries
                  straight over. Any outside company that handles personal information for you,
                  including whoever collects and destroys your confidential paper and old devices,
                  should be checked, approved and put under a proper contract before it starts, and
                  checked again afterwards.
                </p>

                <section id="section-1" aria-labelledby="section-1-h">
                  <h2 id="section-1-h" className={h2Class}>
                    What happened at the Met?
                  </h2>
                  <p className={bodyClass}>
                    According to the ICO, Met officers and staff used an unapproved retail crime
                    intelligence sharing platform between March 2023 and September 2024. Around 500
                    accounts were created and 375 were actively used. Between January and September
                    2024, personal information about 179 people, three of them under 18, was
                    uploaded to help identify people suspected of shoplifting. That included names,
                    dates of birth and, in a small number of cases, home addresses.
                  </p>
                  <p className={bodyClass}>The ICO found that the force:</p>
                  <ul className={listClass}>
                    <li>
                      processed personal information unlawfully, without consent and where sharing
                      was not necessary for law enforcement purposes
                    </li>
                    <li>
                      did not have appropriate measures in place to protect the information or to
                      make sure its approval process for third-party technology was followed
                    </li>
                    <li>
                      did not complete a data protection impact assessment (DPIA) before high-risk
                      processing
                    </li>
                    <li>
                      still had not done enough to make sure only approved third-party technology is
                      used
                    </li>
                  </ul>
                  <p className={bodyClass}>
                    The Met suspended the platform in September 2024 and brought it back in January
                    2026 with stronger governance, staff training, formal contractual controls and a
                    completed DPIA. The ICO still decided action was needed on the wider question of
                    how the force approves outside services. The enforcement notice requires it to
                    strengthen how it identifies, assesses and approves third-party services, review
                    its controls, improve staff awareness and regularly test that its measures work.
                  </p>
                  <p className={bodyClass}>
                    A reprimand is not a fine, but the enforcement notice sets out what must change.
                  </p>
                </section>

                <section id="section-2" aria-labelledby="section-2-h">
                  <h2 id="section-2-h" className={h2Class}>
                    Why it matters beyond policing
                  </h2>
                  <p className={bodyClass}>
                    David Doodson, the ICO's Head of Investigations, said the action "should send a
                    message across the policing sector, and beyond", and that before introducing new
                    systems or platforms, organisations "must properly assess the risks, put
                    appropriate safeguards in place and ensure effective oversight."
                  </p>
                  <p className={bodyClass}>
                    That applies to plenty of everyday suppliers, not just software. A shredding
                    company holding sacks of patient letters, payroll records or HR files is
                    handling personal information on your behalf. So is whoever takes away old
                    laptops and hard drives. If one of them gets it wrong, the ICO's guidance is
                    clear that the responsibility still sits with you as the organisation in control
                    of the data.
                  </p>
                </section>

                <section id="section-3" aria-labelledby="section-3-h">
                  <h2 id="section-3-h" className={h2Class}>
                    What does UK guidance expect when you use a supplier?
                  </h2>
                  <p className={bodyClass}>
                    The ICO's guidance on controllers and processors says you must only use a
                    processor that can show it has the expertise, resources and reliability to
                    protect personal information properly. Once you have chosen one, you need a
                    written contract, and the UK GDPR sets out minimum terms, including:
                  </p>
                  <ul className={listClass}>
                    <li>processing only on your documented instructions</li>
                    <li>confidentiality commitments from the supplier's staff</li>
                    <li>appropriate security measures</li>
                    <li>rules on using subcontractors</li>
                    <li>what happens to the data at the end of the contract</li>
                    <li>your right to audits and inspections</li>
                  </ul>
                  <p className={bodyClass}>
                    Your job does not end at signing. The ICO says you should keep checking that the
                    supplier is doing what it agreed. Its disposal checklists also expect contracts
                    with disposal providers to cover security, accountability and your right to
                    audit, and expect you to get evidence, such as destruction certificates, that
                    the waste was actually destroyed. Our guide to{" "}
                    <A href="https://preserveearth.co.uk/blog/ico-confidential-waste-disposal-guide">
                      what the ICO expects when you dispose of confidential waste
                    </A>{" "}
                    goes through those checklists in plain English.
                  </p>
                </section>

                <section id="section-4" aria-labelledby="section-4-h">
                  <h2 id="section-4-h" className={h2Class}>
                    What this means for your business
                  </h2>
                  <p className={bodyClass}>
                    The Met had a platform in use before anyone approved it. The disposal version of
                    that is a cleaner, an office manager or a site lead booking whoever is cheapest
                    or closest, with no one checking first. A short supplier checklist closes that
                    gap.
                  </p>
                  <ol className="mt-6 list-decimal space-y-3 pl-6 text-base leading-[1.7] text-charcoal/80 marker:text-moss">
                    <li>
                      <strong>Know who handles your waste.</strong> List every company that collects
                      paper, devices or media from any of your sites, including one-off clearances
                      and office moves.
                    </li>
                    <li>
                      <strong>Approve before you book.</strong> Make one person or team responsible
                      for signing off a disposal supplier before it is used, the same way you would
                      approve software.
                    </li>
                    <li>
                      <strong>Put it in writing.</strong> Make sure the contract covers security,
                      confidentiality, subcontracting, your right to audit and proof of destruction.
                    </li>
                    <li>
                      <strong>Match records to collections.</strong> Keep a log of what goes out,
                      and check every destruction record against it.
                    </li>
                    <li>
                      <strong>Review it regularly.</strong> Check suppliers again when contracts
                      renew or when what you send them changes, for example a move from paper to
                      devices.
                    </li>
                  </ol>
                </section>

                <section id="faq" aria-labelledby="faq-h">
                  <h2 id="faq-h" className={h2Class}>
                    Frequently asked questions
                  </h2>
                  <div className="mt-8 divide-y divide-charcoal/10 border-y border-charcoal/10">
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        What should a contract with a shredding provider include?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        The ICO expects contracts with disposal providers to cover security
                        measures, accountability and your right to audit the provider. Where the
                        provider is processing personal information for you, UK GDPR also requires
                        terms such as acting only on your instructions, staff confidentiality, rules
                        on subcontractors and what happens at the end of the contract.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        Do we need a DPIA to change our disposal supplier?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Not automatically. ICO guidance says a DPIA is required where processing is
                        likely to result in a high risk to people's rights and freedoms, and it is
                        up to you to judge that. If you are unsure, the ICO recommends doing one
                        anyway.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        How do we check our disposal provider is doing what they say?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        Keep a log of everything you send for destruction and check each destruction
                        record against it. The ICO suggests naming a staff member to do this, and
                        your contract should give you the right to audit the provider.
                      </p>
                    </div>
                    <div className="py-6">
                      <h3 className="font-serif text-xl leading-snug text-moss">
                        Does the Met case mean the police had a waste disposal problem?
                      </h3>
                      <p className="mt-3 text-base leading-[1.8] text-charcoal/80">
                        No. The case was about sharing personal information on an unapproved online
                        platform. We use it here because the ICO's point about assessing and
                        approving third parties applies to any supplier that handles personal
                        information, including disposal providers.
                      </p>
                    </div>
                  </div>
                </section>

                <aside
                  aria-labelledby="cta-h"
                  className="mt-20 border-y border-charcoal/15 bg-sage-light/40 px-8 py-10"
                >
                  <h2 id="cta-h" className="font-serif text-2xl text-charcoal">
                    Reviewing your disposal supplier?
                  </h2>
                  <p className="mt-3 max-w-[58ch] text-sm leading-relaxed text-charcoal/70">
                    We arrange{" "}
                    <A href="/services/confidential-waste-disposal">
                      secure confidential paper shredding
                    </A>{" "}
                    and <A href="/services/it-and-media-recycling">IT equipment destruction</A>{" "}
                    across the UK, with locked consoles for scheduled collections or one-off
                    collections when you need them, and an electronic note of secure destruction
                    after every collection for your records. Tell us what you need to dispose of and
                    how often, and we will come back to you with a tailored quote.
                  </p>
                  <a
                    href="https://preserveearth.co.uk/services/confidential-waste-disposal#enquire"
                    className="mt-6 inline-flex items-center gap-3 rounded-sm bg-moss px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-charcoal"
                  >
                    Request a tailored quote
                  </a>
                </aside>

                <div className="mt-12 text-sm leading-relaxed text-charcoal/70">
                  <h2 className="text-[10px] font-semibold uppercase tracking-[0.25em] text-moss">
                    Sources
                  </h2>
                  <ul className="mt-3 space-y-2">
                    <li>
                      ICO, "ICO takes action against Metropolitan Police Service" (14 October 2026):{" "}
                      <a
                        href="https://ico.org.uk/about-the-ico/media-centre/news-and-blogs/2026/10/ico-takes-action-against-metropolitan-police-service-over-information-governance-failures/"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://ico.org.uk/about-the-ico/media-centre/news-and-blogs/2026/10/ico-takes-action-against-metropolitan-police-service-over-information-governance-failures/
                      </a>
                    </li>
                    <li>
                      ICO, Metropolitan Police Service reprimand (23 September 2026):{" "}
                      <a
                        href="https://ico.org.uk/action-weve-taken/enforcement/2026/09/metropolitan-police-service-reprimand"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://ico.org.uk/action-weve-taken/enforcement/2026/09/metropolitan-police-service-reprimand
                      </a>
                    </li>
                    <li>
                      ICO, Responsibilities and liabilities for controllers using a processor:{" "}
                      <a
                        href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/contracts-and-liabilities-between-controllers-and-processors-multi/responsibilities-and-liabilities-for-controllers-using-a-processor/"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/contracts-and-liabilities-between-controllers-and-processors-multi/responsibilities-and-liabilities-for-controllers-using-a-processor/
                      </a>
                    </li>
                    <li>
                      ICO, What needs to be included in the contract?:{" "}
                      <a
                        href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/contracts-and-liabilities-between-controllers-and-processors-multi/what-needs-to-be-included-in-the-contract/"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/contracts-and-liabilities-between-controllers-and-processors-multi/what-needs-to-be-included-in-the-contract/
                      </a>
                    </li>
                    <li>
                      ICO, When do we need to do a DPIA?:{" "}
                      <a
                        href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/data-protection-impact-assessments-dpias/when-do-we-need-to-do-a-dpia/"
                        target="_blank"
                        rel="noopener"
                        className={`${linkClass} break-all`}
                      >
                        https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/accountability-and-governance/data-protection-impact-assessments-dpias/when-do-we-need-to-do-a-dpia/
                      </a>
                    </li>
                  </ul>
                  <p className="mt-6 italic">
                    <em>
                      This article is general information based on ICO announcements and published
                      ICO guidance, some of which the ICO says is under review following the Data
                      (Use and Access) Act. It is not legal advice.
                    </em>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>

        <RelatedArticles
          slugs={[
            "ico-confidential-waste-disposal-guide",
            "confidential-paper-binned-by-mistake-zagreb-lesson",
          ]}
        />
        <RelatedServices slugs={["confidential-waste-disposal"]} />
      </main>

      <SiteFooter />
    </div>
  );
}
